<script setup lang="ts">
import { ANNOUNCE_BAR } from '~/shared/lib/announce-bar.const';
import { declineWords } from '~/shared/utils/declineWords';

const props = defineProps<{
  days: number;
  hours: number;
}>();

const units = computed(() => [
  {
    value: props.days,
    caption: declineWords(props.days, ['день', 'дня', 'дней']),
  },
  {
    value: props.hours,
    caption: declineWords(props.hours, ['час', 'часа', 'часов']),
  },
]);
</script>

<template>
  <a
    :class="$style.announce"
    :href="ANNOUNCE_BAR.link"
    target="_blank"
    rel="noopener noreferrer"
  >
    <Icon
      :name="`my-icon:${ANNOUNCE_BAR.logo}`"
      :class="$style.announce__logo"
    />

    <span :class="$style.announce__body">
      <span :class="$style.announce__label">{{ ANNOUNCE_BAR.label }}</span>

      <span :class="$style.announce__timer">
        <template v-for="(unit, index) in units" :key="`announceUnit_${index}`">
          <span v-if="index > 0" :class="$style.announce__separator">:</span>

          <span :class="$style.announce__unit">
            <span :class="$style.announce__value">{{ unit.value }}</span>
            <span :class="$style.announce__caption">{{ unit.caption }}</span>
          </span>
        </template>
      </span>
    </span>
  </a>
</template>

<style module lang="scss">
.announce {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-1-5x);
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  padding: 0 var(--space-2-5x);
  background-color: var(--color-awards-gold);
  color: var(--color-white);
  text-decoration: none;

  &:hover {
    color: var(--color-white);
  }

  &__logo {
    display: block;
    flex-shrink: 0;
    /* aspect-ratio, чтобы Icon не сплющил логотип своими размерами */
    height: 32px;
    width: auto;
    aspect-ratio: 95 / 32;
    color: var(--color-white);
  }

  &__body {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 11px;
    min-width: 0;
  }

  &__label {
    @include text_caption1_medium;
    line-height: 1;
    text-transform: uppercase;
    white-space: nowrap;
    /* Жертвуем подписью, а не таймером, если не хватает места */
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__timer {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: var(--space-0-5x);
    flex-shrink: 0;
  }

  &__unit {
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-content: center;
    gap: 3px;
  }

  &__value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    padding: var(--space-0-25x) var(--space-0-5x);
    border-radius: 2px;
    background-image: linear-gradient(
      180deg,
      rgba(255, 243, 195, 0.3) 0%,
      rgba(223, 213, 176, 0.3) 50%,
      rgba(203, 193, 152, 0.3) 50%,
      rgba(214, 206, 177, 0.3) 100%
    );
    @include text_body2_medium;
    /* Иначе блок дёргается при смене числа */
    font-variant-numeric: tabular-nums;
  }

  &__caption {
    font-weight: 500;
    font-size: 7px;
    line-height: 1;
    text-transform: uppercase;
  }

  &__separator {
    font-weight: 500;
    font-size: 8px;
    line-height: 1;
    align-self: flex-start;
    margin-top: 7px;
  }
}

@media (max-width: 1379px) {
  .announce {
    gap: var(--space-1x);
    padding: 0 var(--space-2x);

    &__logo {
      height: 27px;
    }

    &__body {
      gap: var(--space-1x);
    }
  }
}

@media (max-width: 1023px) {
  .announce {
    height: 48px;
    padding: var(--space-1x) var(--space-2-5x);

    &__logo {
      height: 25px;
    }

    &__label {
      font-size: 10px;
    }
  }
}

@media (max-width: 479px) {
  .announce {
    gap: var(--space-0-5x);
    padding: var(--space-1x) var(--space-2x);

    &__logo {
      height: 22px;
    }

    &__body {
      gap: var(--space-0-5x);
    }

    &__label {
      font-size: 9px;
      min-width: 0;
      flex-shrink: 1;
    }
  }
}
</style>
